import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import GenericTable from "@components/tables/GenericTable";
import { fetchCompletedFiles, sendToDevnagri } from "@store/thunks/dashboardThunk";
import { setSelectedTargetId, setSeletedFileId, setTargetLang } from "@store/slices/dashboardSlice";
import { toast } from "react-toastify";
import { restrictSpecialCharacters } from "@/utils/constants";

// Custom debounce hook
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

const GAValidatedFilesTable = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const inputRef = useRef(null);
  const { completedFiles = [], selectedFileId } = useSelector((store) => store.dashboard);
  const [inputValue, setInputValue] = useState("");
  const debouncedSearchQuery = useDebounce(inputValue.toLowerCase(), 300);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [hasNextPage, setHasNextPage] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sendingFiles, setSendingFiles] = useState({});
  const [popup, setPopup] = useState({ isOpen: false, type: "", message: "" });

  useEffect(() => {
    const fetchData = async () => {
      if (!selectedFileId) {
        setError("No folder selected.");
        toast.error("No folder selected.");
        return;
      }
      try {
        setLoading(true);
        setError(null);
        const res = await dispatch(fetchCompletedFiles('GA', selectedFileId, debouncedSearchQuery, pageIndex, pageSize));
        if (Array.isArray(res?.data)) {
          if (res.data.length < pageSize) {
            setHasNextPage(false);
          } else {
            setHasNextPage(true);
          }
        }
      } catch (err) {
        console.error("[GAValidatedFilesTable] Error fetching files:", err);
        setError("Failed to fetch files. Please try again.");
        toast.error("Failed to fetch files.");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [dispatch, selectedFileId, debouncedSearchQuery, pageIndex, pageSize]);

  const handleClickBack = () => {
    navigate(-1);
  };

  const handleClickHome = () => {
    navigate("/superAdmin");
  };

  const handleFileClick = ({ row }) => {
    const fileId = row.original?.fileId;
    if (!fileId) {
      console.error("[GAValidatedFilesTable] File ID not found in row data:", row);
      toast.error("Error: Could not determine File ID");
      return;
    }

    dispatch(setSeletedFileId(fileId));
    dispatch(setTargetLang(row.original?.targetName || null));
    dispatch(setSelectedTargetId(row.original?.targetId));
    navigate("/superAdmin/ga/approved-projects/folders/approved-files/sentences");
  };

  const handleSendToDevnagri = async ({ row }) => {
    const fileId = row.original?.fileId;
    const sourceLanguageId = row.original?.sourceId;
    const targetLanguageId = row.original?.targetId;
    if (!fileId) {
      console.error("[GAValidatedFilesTable] File ID not found for sending:", row);
      toast.error("Error: Could not determine File ID", {
        style: {
          background: "#dc3545", // Red for error
          color: "#ffffff",
        },
      });
      return;
    }

    try {
      setSendingFiles((prev) => ({ ...prev, [fileId]: true }));
      const result = await dispatch(sendToDevnagri(fileId, sourceLanguageId, targetLanguageId));
      console.log("Result from sendToDevnagri:", result);

      // Check if the API response indicates success
      if (result.success) {
        toast.success(`File ${row.original?.fileName || "unknown"} sent to Devnagri successfully`, {
          style: {
            background: "#28a745", // Green for success
            color: "#ffffff",
          },
        });
      } else {

      }
    } catch (err) {
      console.error("[GAValidatedFilesTable] Error sending file to Devnagri:", err);
      toast.error(err.message || "Failed to send file to Devnagri. Please try again.", {
        style: {
          background: "#dc3545", // Red for error
          color: "#ffffff",
        },
      });
    } finally {
      setSendingFiles((prev) => ({ ...prev, [fileId]: false }));
    }
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    if (restrictSpecialCharacters(newValue) || newValue === "") {
      setInputValue(newValue);
      setPageIndex(0);
      setHasNextPage(true); // Reset search → assume more pages exist
    } else {
      setPopup({
        isOpen: true,
        type: "error",
        message: "Special characters are not allowed. Use only letters, numbers, and spaces.",
      });
    }
  };

  const handlePopupClose = () => {
    setPopup({ isOpen: false, type: "", message: "" });
  };

  const sentenceColumns = [
    {
      header: "Sr. No.",
      cell: ({ row, table }) => {
        const rowIndex = row.index;
        return (
          <div className="text-center font-['Semantic/Strong']">
            {pageIndex * pageSize + rowIndex + 1}
          </div>
        );
      },
      cellClassName: "text-center",
    },
    {
      header: "File Name",
      accessorKey: "fileName",
      cell: ({ row }) => (
        <button
          onClick={() => handleFileClick({ row })}
          className="max-w-md overflow-hidden text-ellipsis text-[#ec7026] hover:text-[#d85d1a] hover:underline font-['Semantic/Strong']"
        >
          {row.original.fileName || "N/A"}
        </button>
      ),
    },
    {
      header: "Translator Name",
      accessorKey: "translatorUserName",
      cell: ({ row }) => (
        <div className="max-w-md font-['Semantic/Strong']">
          {row.original.translatorUserName || "N/A"}
        </div>
      ),
    },
    {
      header: "Validation User Name",
      accessorKey: "validationUserName",
      cell: ({ row }) => (
        <div className="max-w-md font-['Semantic/Strong']">
          {row.original.validationUserName || "N/A"}
        </div>
      ),
    },
    {
      header: "Source Language",
      accessorKey: "sourceName",
      cell: ({ row }) => (
        <div className="max-w-md font-['Semantic/Strong']">
          {row.original.sourceName || "N/A"}
        </div>
      ),
    },
    {
      header: "Target Language",
      accessorKey: "targetName",
      cell: ({ row }) => (
        <div className="max-w-md font-['Semantic/Strong']">
          {row.original.targetName || "N/A"}
        </div>
      ),
    },
    {
      header: "Total Sentences",
      accessorKey: "totalSentences",
      cell: ({ row }) => (
        <div className="text-center font-['Semantic/Strong']">
          {row.original.totalSentences !== undefined ? row.original.totalSentences : "N/A"}
        </div>
      ),
      cellClassName: "text-center",
    },
    {
      header: "Translated Sentences Count",
      accessorKey: "translatedSentenceCount",
      cell: ({ row }) => (
        <div className="text-center font-['Semantic/Strong']">
          {row.original.translatedSentenceCount !== undefined ? row.original.translatedSentenceCount : "N/A"}
        </div>
      ),
      cellClassName: "text-center",
    },
    {
      header: "Validated Sentences Count",
      accessorKey: "validatedSentenceCount",
      cell: ({ row }) => (
        <div className="text-center font-['Semantic/Strong']">
          {row.original.validatedSentenceCount !== undefined ? row.original.validatedSentenceCount : "N/A"}
        </div>
      ),
      cellClassName: "text-center",
    },
    {
      header: "Rejected Sentences Count",
      accessorKey: "rejectedSentenceCount",
      cell: ({ row }) => (
        <div className="text-center font-['Semantic/Strong']">
          {row.original.rejectedSentenceCount !== undefined ? row.original.rejectedSentenceCount : "N/A"}
        </div>
      ),
      cellClassName: "text-center",
    },
    {
      header: "Assign",
      cell: ({ row }) => (
        <button
          onClick={() => handleSendToDevnagri({ row })}
          disabled={sendingFiles[row.original?.fileId] || row.original?.devStatus === 'PENDING' || row.original?.devStatus === 'SUBMITTED_TO_CDAC'}
          className={`inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-['Semantic/Strong'] rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#ec7026] transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed`}
        >
          {sendingFiles[row.original?.fileId] ? (
            <svg
              className="animate-spin -ml-1 mr-2 h-4 w-4 text-[#ec7026]"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
          ) : null}
          <span>{row.original?.devStatus === 'PENDING' ? "Sent" : row.original?.devStatus === 'SUBMITTED_TO_CDAC' ? "Final Submit done" : "Send"}</span>
        </button>
      ),
      cellClassName: "text-center",
    },
  ];

  // Client-side filtering as a fallback
  const filteredFiles = debouncedSearchQuery
    ? completedFiles.filter(
      (file) =>
        file.fileName?.toLowerCase().includes(debouncedSearchQuery) ||
        file.translatorUserName?.toLowerCase().includes(debouncedSearchQuery) ||
        file.validationUserName?.toLowerCase().includes(debouncedSearchQuery) ||
        file.sourceName?.toLowerCase().includes(debouncedSearchQuery) ||
        file.targetName?.toLowerCase().includes(debouncedSearchQuery) ||
        String(file.totalSentences)?.includes(debouncedSearchQuery) ||
        String(file.translatedSentenceCount)?.includes(debouncedSearchQuery) ||
        String(file.validatedSentenceCount)?.includes(debouncedSearchQuery) ||
        String(file.rejectedSentenceCount)?.includes(debouncedSearchQuery)
    )
    : completedFiles;

  if (loading) {
    return (
      <div className="w-full p-4 sm:p-6 lg:p-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-gray-200 rounded w-full"></div>
            <div className="h-8 bg-gray-200 rounded w-full"></div>
            <div className="h-8 bg-gray-200 rounded w-full"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full p-4 sm:p-6 lg:p-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg">
            <p className="font-['Semantic/Strong']">{error}</p>
            <p className="font-['Semantic/Strong'] mt-2">Please try again later or contact support.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-[calc(100vh-4rem)] bg-gray-50/50 mt-14">
      <div className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-10">
        <div className="w-full px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col md:flex-row gap-4 justify-between">
            <div className="flex flex-col">
              <h1 className="text-2xl font-['Semantic/Strong'] font-bold text-gray-900">
                <span className="text-[#ec7026] shadow-sm">GA Solutions</span> Completed Files
              </h1>
              <p className="text-sm text-gray-500 font-['Semantic/Strong']">
                View and manage completed GA Solutions files
              </p>
            </div>

            <div className="flex flex-wrap sm:flex-nowrap gap-2 items-center">
              <button
                onClick={handleClickHome}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-['Semantic/Strong'] rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#ec7026] transition-colors duration-200"
              >
                <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                  />
                </svg>
                <span>Home</span>
              </button>

              <button
                onClick={handleClickBack}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-['Semantic/Strong'] rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#ec7026] transition-colors duration-200"
              >
                <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M10 19l-7-7m0 0l7-7m-7 7h18"
                  />
                </svg>
                <span>Back</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {popup.isOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 max-w-md w-full mx-4">
            <h2 className="text-lg font-['Semantic/Strong'] font-bold text-gray-900">
              {popup.type === "error" ? "Error" : "Info"}
            </h2>
            <p className="mt-2 text-sm text-gray-500 font-['Semantic/Strong']">
              {popup.message}
            </p>
            <div className="mt-4 flex justify-end">
              <button
                onClick={handlePopupClose}
                className="inline-flex items-center px-4 py-2 border border-[#ec7026] shadow-sm text-sm font-['Semantic/Strong'] rounded-lg text-white bg-[#ec7026] hover:bg-[#d85d1a] focus:outline-none focus:ring-2 focus:ring-[#ec7026]"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="w-full p-4 sm:p-6 lg:p-8">
        <div className="flex justify-end mb-6">
          <div className="relative w-full sm:w-64">
            <input
              ref={inputRef}
              type="text"
              placeholder="Search files..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#ec7026] text-base font-['Semantic/Strong'] text-gray-700 pl-10"
              value={inputValue}
              onChange={handleInputChange}
            />
            <svg
              className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="w-full overflow-x-auto">
            {filteredFiles.length === 0 ? (
              <div className="p-6">
                <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded-lg">
                  <p className="font-['Semantic/Strong']">No files found.</p>
                  <p className="font-['Semantic/Strong'] mt-2">
                    No completed GA files match the search criteria.
                  </p>
                </div>
              </div>
            ) : (
              <GenericTable
                data={[...filteredFiles].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))}
                columns={sentenceColumns}
                showPagination={true}
                manualPagination={true}
                pageIndex={pageIndex}
                pageSize={pageSize}
                hasNextPage={hasNextPage}
                onPaginationChange={({ pageIndex, pageSize }) => {
                  setPageIndex(pageIndex);
                  setPageSize(pageSize);
                }}
                emptyMessage="No completed GA files available"
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GAValidatedFilesTable;